import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { api, monthLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Settings2, Mail, Trash2, Users, Loader2 } from "lucide-react";

export const UserSettingsDialog = () => {
  const qc = useQueryClient();
  const { users, setUsers, activeUser, setActiveUser, month } = useApp();
  const [open, setOpen] = useState(false);
  const [names, setNames] = useState(users);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const [recipients, setRecipients] = useState(["", ""]);

  React.useEffect(() => {
    if (open) {
      setNames(users);
      const r = settings?.recipients || [];
      setRecipients([r[0] || "", r[1] || ""]);
    }
  }, [open, users, settings]);

  const namesMut = useMutation({
    mutationFn: (list) => api.updateSettings({ users: list }),
    onSuccess: (doc) => {
      const list = doc.users;
      const activeIdx = users.indexOf(activeUser);
      setUsers(list);
      const newActive = activeIdx >= 0 ? list[activeIdx] : list[0];
      setActiveUser(newActive);
      localStorage.setItem("activeUser", newActive);
      qc.invalidateQueries();
      toast.success("Names updated");
    },
    onError: () => toast.error("Could not update names"),
  });

  const recipientsMut = useMutation({
    mutationFn: (list) => api.updateSettings({ recipients: list }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Recipients saved"); },
  });

  const emailMut = useMutation({
    mutationFn: () => api.sendEmailSummary(month),
    onSuccess: (res) => toast.success(`Summary sent`, { description: `Emailed to ${res.recipients.join(", ")}` }),
    onError: (e) => toast.error("Could not send email", { description: e?.response?.data?.detail || "Check your Resend API key." }),
  });

  const resetMut = useMutation({
    mutationFn: () => api.resetData(),
    onSuccess: (res) => { qc.invalidateQueries(); toast.success(`Cleared ${res.cleared_transactions} transactions`); setOpen(false); },
  });

  const saveNames = () => {
    const cleaned = names.map((n) => n.trim());
    if (cleaned.some((n) => !n)) return toast.error("Both names are required");
    namesMut.mutate(cleaned);
  };
  const saveRecipients = () => recipientsMut.mutate(recipients.map((r) => r.trim()).filter(Boolean));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-user-settings" className="text-slate-500 hover:text-[#f97316] transition-colors p-1">
          <Settings2 className="w-3.5 h-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#141a26] border-[#2a364f] text-slate-100 max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Settings</DialogTitle>
          <DialogDescription className="text-slate-400">Manage members, the monthly email summary and your data.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-200"><Users className="w-4 h-4 text-[#f97316]" /> Household members</div>
          {names.map((n, i) => (
            <div key={i} className="space-y-1">
              <Label className="text-xs text-slate-400">Person {i + 1}</Label>
              <Input data-testid={`user-name-input-${i}`} value={n} onChange={(e) => setNames((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} className="bg-[#0a0e14] border-[#2a364f]" style={{ borderLeft: `3px solid ${i === 0 ? "#f97316" : "#22c55e"}` }} />
            </div>
          ))}
          <Button data-testid="save-user-names" onClick={saveNames} size="sm" className="bg-[#f97316] hover:bg-[#ea580c] text-white w-full">Save names</Button>
        </div>

        <Separator className="bg-[#2a364f]" />

        <div className="space-y-3 py-1">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-200"><Mail className="w-4 h-4 text-[#22c55e]" /> Monthly email summary</div>
          {recipients.map((r, i) => (
            <Input key={i} data-testid={`recipient-input-${i}`} value={r} onChange={(e) => setRecipients((arr) => arr.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`recipient ${i + 1} email`} className="bg-[#0a0e14] border-[#2a364f]" />
          ))}
          <div className="flex gap-2">
            <Button data-testid="save-recipients" onClick={saveRecipients} size="sm" variant="outline" className="border-[#2a364f] text-slate-300 hover:bg-[#2a364f] flex-1">Save</Button>
            <Button data-testid="send-summary-button" onClick={() => emailMut.mutate()} disabled={emailMut.isPending} size="sm" className="bg-[#22c55e] hover:bg-[#16a34a] text-white flex-1 gap-1.5">
              {emailMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send {monthLabel(month).split(" ")[0]} summary
            </Button>
          </div>
        </div>

        <Separator className="bg-[#2a364f]" />

        <div className="space-y-2 py-1">
          <div className="flex items-center gap-2 text-sm font-medium text-red-400"><Trash2 className="w-4 h-4" /> Danger zone</div>
          <p className="text-xs text-slate-500">Clear all logged transactions to start fresh with your own spending. Your categories and budgets stay intact.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="reset-data-button" size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 w-full">Clear all transactions</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#141a26] border-[#2a364f] text-slate-100">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">Clear all transactions?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">This permanently deletes every transaction and rollover history across all months. Categories, budgets and recurring bills are kept. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-transparent border-[#2a364f] text-slate-300 hover:bg-[#2a364f]">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-reset-button" onClick={() => resetMut.mutate()} className="bg-red-500 hover:bg-red-600 text-white">Clear everything</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </DialogContent>
    </Dialog>
  );
};
